/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { bool, params, shape, string, useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useImpression } from '@thd-olt-component-react/impression';
import { RatingMeter, Typography } from '@one-thd/sui-atomic-components';
import { getCustomUrlWithAnalytics } from '../../productPodUtils';
import { PRODUCT_POD_V7_CLICK } from '../../constants';

export const ProductRating = (
  {
    itemId, target = null, onClick = null, openDetailsInNewTab = true, isIframe = false, iframeAnalyticsData = null, merchQueryParam = null, onlyReviewCount = false
  }) => {
  const { hosts } = useContext(ExperienceContext);
  const { additionalData = {} } = useImpression({
    data: {
      id: itemId,
      component: 'ProductRating',
      name: 'ProductRating',
      position: 0
    }
  });

  const { parent } = additionalData || {};

  const [sponsoredValues, setSponsoredValues] = useState({});
  const [reviewsData, setReviewsData] = useState({});

  const { data } = useDataModel('product', {
    variables: {
      itemId,
    },
  });

  const { product } = data || {};
  const { info, identifiers, reviews } = product || {};

  useEffect(() => {
    if (reviews) {
      setReviewsData({
        totalReviews: reviews?.ratingsReviews?.totalReviews,
        averageRating: reviews?.ratingsReviews?.averageRating,
        canonicalUrl: identifiers?.canonicalUrl,
        productType: identifiers?.productType,
      });
    }
  }, [reviews, identifiers?.canonicalUrl, identifiers?.productType]);

  useEffect(() => {
    if (info) {
      setSponsoredValues({
        isSponsored: sponsoredValues?.isSponsored || info?.isSponsored,
        sponsoredBeacon: sponsoredValues?.sponsoredBeacon || info?.sponsoredBeacon,
        sponsoredMetadata: sponsoredValues?.sponsoredMetadata || info?.sponsoredMetadata,
      });
    }
  }, [info?.isSponsored, info?.sponsoredBeacon, info?.sponsoredMetadata]);

  let totalReviews = reviews?.ratingsReviews?.totalReviews || reviewsData?.totalReviews;
  let averageRating = reviews?.ratingsReviews?.averageRating || reviewsData?.averageRating;

  if (!totalReviews && !averageRating && !reviews) return null;

  const canonicalUrl = identifiers?.canonicalUrl || reviewsData?.canonicalUrl;
  const productType = identifiers?.productType || reviewsData?.productType;
  const averageRatingHasValue = Number(averageRating) > 0;

  const hasRatings = Boolean(
    (typeof averageRating === 'string' || typeof averageRating === 'object')
    && (typeof totalReviews === 'string' || typeof totalReviews === 'object')
  );

  if (!hasRatings) return null;

  totalReviews = parseInt(totalReviews, 10);
  averageRating = Number(averageRating).toFixed(1);

  const productURL = getCustomUrlWithAnalytics({
    productType,
    blindsHost: hosts?.customBlinds,
    canonicalUrl,
    info,
    sponsoredValues,
    merchQueryParam
  });

  const handleClick = (event, action = null) => {
    const productRatingEventData = {
      podAction: 'product rating',
      podAnchorSku: itemId,
      target,
      parent
    };

    if (isIframe && typeof window !== 'undefined' && window.top?.LIFE_CYCLE_EVENT_BUS) {
      window.top.LIFE_CYCLE_EVENT_BUS.trigger('cartItem.DD_EVENT', iframeAnalyticsData);
    } else if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      setTimeout(() => {
        window.LIFE_CYCLE_EVENT_BUS.trigger(PRODUCT_POD_V7_CLICK, productRatingEventData);
      }, 0);
    }
    if (onClick) {
      onClick(event, product, action);
    }
    if (event?.nativeEvent) {
      event.nativeEvent._podAnalyticsHandled = true;
    }
  };

  const customReviews = (
    <>
      {averageRatingHasValue
        && (
          <Typography color="primary" component="span" height="tight" variant="body-xs">({Number(averageRating)} /{"\u00A0"}<Typography color="subtle" component="span" height="tight" variant="body-xs">
              {totalReviews}
            </Typography>)
          </Typography>
        )}
    </>
  );

  const customReviewsV2 = (
    <Typography color="primary" component="span" height="tight" variant="body-xs">
      <Typography color="subtle" component="span" height="tight" variant="body-xs">
        ({Number.isNaN(totalReviews) ? 0 : totalReviews})
      </Typography>
    </Typography>
  );

  return (
    <div className="[&_a]:sui-p-0 [&_svg]:sui-w-3">
      <RatingMeter
        edge="end"
        size="small"
        target={target}
        onClick={handleClick}
        value={averageRating}
        label={onlyReviewCount ? customReviewsV2 : customReviews}
        data-testid="product-pod__ratings-link"
        /*
        * SEA2-677: we're stopping the use of the productURL for now,
        * since it's not taking really taking to the Customer Reviews section.
        * We'll circle back to this later.
        */
        // href={openDetailsInNewTab ? `${productURL}#product-section-rr` : ''}
        href={productURL ?? ''}
        RatingProps={{
          color: 'brand',
        }}
      />
    </div>
  );
};

ProductRating.displayName = 'ProductRating';

ProductRating.dataModel = {
  product: params({ itemId: string().isRequired() }).shape({
    dataSource: string(),
    itemId: string(),
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string(),
        totalReviews: string(),
      }),
    }),
    info: shape({
      isSponsored: bool(),
      sponsoredBeacon: shape({
        onClickBeacon: string(),
        onViewBeacon: string(),
      }),
      sponsoredMetadata: shape({
        campaignId: string(),
        placementId: string(),
        slotId: string(),
      }),
      globalCustomConfigurator: shape({
        customExperience: string(),
      }),
    }),
    identifiers: shape({
      canonicalUrl: string(),
      productType: string(),
    }),
  }),
};

ProductRating.propTypes = {
  itemId: PropTypes.string.isRequired,
  target: PropTypes.string,
  onClick: PropTypes.func,
  openDetailsInNewTab: PropTypes.bool,
  iframeAnalyticsData: PropTypes.object,
  isIframe: PropTypes.bool,
  merchQueryParam: PropTypes.string,
  onlyReviewCount: PropTypes.bool
};
